import { shadows } from '@ardoq/design-tokens';
import { Box, Inline } from '@ardoq/layout';
import { Link } from '@ardoq/link';
import { Editor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useCallback, useEffect, useState } from 'react';
import { ButtonSize, GhostButton } from '@ardoq/button';
import { LinkForm, LinkFormLabels } from '../toolbar/LinkForm';
import { getTextFromSelection } from './extensionUtils';
import { ToolbarDivider } from '../toolbar/Toolbar';

export type LinkBubbleMenuLabels = {
  editLink: string;
  removeLink: string;
  goToLink: string;
};

export const defaultLinkBubbleMenuLabels: LinkBubbleMenuLabels = {
  editLink: 'Edit link',
  removeLink: 'Remove link',
  goToLink: 'Go to link',
};

interface LinkBubbleMenuProps {
  editor: Editor | null;
  labels?: LinkBubbleMenuLabels;
  linkFormLabels?: LinkFormLabels;
}

const resolveHref = (editor: Editor) => {
  if (editor.isActive('link')) {
    return editor.getAttributes('link').href;
  }
  if (editor.isActive('presentation')) {
    return editor.getAttributes('presentation').href;
  }
  if (editor.isActive('internalLink')) {
    return editor.getAttributes('internalLink').href;
  }
  return '';
};

export const LinkBubbleMenu = ({
  editor,
  labels = defaultLinkBubbleMenuLabels,
  linkFormLabels,
}: LinkBubbleMenuProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const href = useEditorState({
    editor,
    selector: ctx => {
      if (!ctx.editor) return '';
      return resolveHref(ctx.editor);
    },
  });

  // Reset editing state when the bubble menu hides
  useEffect(() => {
    if (!href) {
      setIsEditing(false);
    }
  }, [href]);

  const shouldShow = useCallback(
    ({ editor, from, to }: { editor: Editor; from: number; to: number }) => {
      if (from === to) {
        return false;
      }
      const href = resolveHref(editor);

      if (!href || !editor.isEditable) {
        return false;
      }

      return true;
    },
    []
  );

  return (
    <BubbleMenu
      editor={editor ?? undefined}
      shouldShow={shouldShow}
      options={{
        placement: 'bottom-start',
      }}
    >
      <Box
        borderWidth={1}
        backgroundColor="surfaceDefault"
        borderRadius="small"
        padding={editor?.isActive('link') ? 'xsmall' : 'small'}
        style={{
          boxShadow: shadows.shadowM,
        }}
      >
        {isEditing && editor?.isActive('link') ? (
          <LinkForm
            isActive
            onSubmit={({ href, text }) => {
              editor.commands.updateLink(href, text);
            }}
            getInitialState={() => {
              const { href } = editor.getAttributes('link');
              return { href, text: getTextFromSelection(editor) };
            }}
            labels={linkFormLabels}
          />
        ) : (
          <Inline align="center" gap="small">
            {editor?.isActive('link') && (
              <GhostButton
                buttonSize={ButtonSize.SMALL}
                onClick={() => setIsEditing(true)}
              >
                {labels.editLink}
              </GhostButton>
            )}
            {editor?.isActive('link') && <ToolbarDivider />}
            {editor?.isActive('link') && (
              <GhostButton
                buttonSize={ButtonSize.SMALL}
                onClick={() => {
                  editor.commands.unsetLink();
                }}
              >
                {labels.removeLink}
              </GhostButton>
            )}
            {editor?.isActive('link') && <ToolbarDivider />}
            <Link href={href || ''} target="_blank">
              {labels.goToLink}
            </Link>
          </Inline>
        )}
      </Box>
    </BubbleMenu>
  );
};
